// =============================================================================
// components/layout/HealthWidget.tsx
// Compact 5-dot health indicator for TopBar — always visible
// Polls /api/mission-control/health every 15s
// Click opens Mission Control (r2-mission-control)
// =============================================================================

import { useState, useEffect, useCallback, type JSX } from 'react';

type HealthStatus = 'healthy' | 'warning' | 'critical' | 'unknown';

interface HealthSnapshot {
  system:   HealthStatus;
  ai:       HealthStatus;
  database: HealthStatus;
  cloud:    HealthStatus;
  security: HealthStatus;
}

const COLOR: Record<HealthStatus, string> = {
  healthy:  '#4ade80',
  warning:  '#fbbf24',
  critical: '#f87171',
  unknown:  'rgba(148,172,210,0.35)',
};

const LABEL: Record<string, string> = {
  system: 'System', ai: 'AI', database: 'Database', cloud: 'Cloud', security: 'Security',
};

const API = (window as Window & { __BACKEND_URL__?: string }).__BACKEND_URL__
  || (import.meta as unknown as Record<string, Record<string, string>>).env?.VITE_BACKEND_URL
  || '';

export function HealthWidget({ onOpenMC }: { onOpenMC?: () => void }): JSX.Element {
  const [health, setHealth] = useState<HealthSnapshot>({
    system: 'unknown', ai: 'unknown', database: 'unknown',
    cloud: 'unknown', security: 'unknown',
  });
  const [lastPoll, setLastPoll] = useState<number>(0);
  const [open, setOpen] = useState(false);

  const poll = useCallback(async () => {
    try {
      const r = await fetch(`${API}/api/mission-control/health`, { signal: AbortSignal.timeout(5000) });
      const d = await r.json();
      const overall = d.overall ?? 'healthy';
      setHealth({
        system:   (d.system?.status   ?? overall) as HealthStatus,
        ai:       (d.ai?.status        ?? 'healthy') as HealthStatus,
        database: (d.database?.status  ?? 'healthy') as HealthStatus,
        cloud:    (d.cloud?.status     ?? 'healthy') as HealthStatus,
        security: (d.security?.status  ?? 'healthy') as HealthStatus,
      });
      setLastPoll(Date.now());
    } catch {
      setHealth((p) => ({ ...p, system: 'unknown', database: 'unknown' }));
    }
  }, []);

  useEffect(() => {
    poll();
    const id = setInterval(poll, 15_000);
    return () => clearInterval(id);
  }, [poll]);

  const worst: HealthStatus = (() => {
    const vals = Object.values(health);
    if (vals.includes('critical')) return 'critical';
    if (vals.includes('warning'))  return 'warning';
    if (vals.includes('unknown'))  return 'unknown';
    return 'healthy';
  })();

  const DOTS = ['system', 'ai', 'database', 'cloud', 'security'] as const;

  return (
    <div style={{ position: 'relative' }}>
      <button
        data-testid="health-widget-btn"
        onClick={() => { onOpenMC ? onOpenMC() : setOpen((v) => !v); }}
        title="R2 Mission Control"
        style={{
          display: 'flex', alignItems: 'center', gap: 5, padding: '5px 10px',
          borderRadius: 8, cursor: 'pointer',
          background: worst === 'healthy' ? 'rgba(74,222,128,0.06)' : worst === 'warning' ? 'rgba(251,191,36,0.08)' : 'rgba(248,113,113,0.08)',
          border: `1px solid ${COLOR[worst]}22`,
          transition: 'all 0.2s',
        }}
      >
        {DOTS.map((key) => (
          <div
            key={key}
            title={`${LABEL[key]}: ${health[key]}`}
            style={{
              width: 7, height: 7, borderRadius: '50%',
              background: COLOR[health[key]],
              boxShadow: health[key] === 'healthy'
                ? `0 0 4px ${COLOR.healthy}50`
                : health[key] === 'critical'
                ? `0 0 6px ${COLOR.critical}80`
                : 'none',
              transition: 'background 0.3s',
            }}
          />
        ))}
      </button>

      {/* Popover (only when no callback) */}
      {!onOpenMC && open && (
        <div style={{
          position: 'absolute', top: '120%', right: 0, zIndex: 1000, minWidth: 200,
          background: 'rgba(8,14,32,0.98)', border: '1px solid rgba(40,68,116,0.5)',
          borderRadius: 12, padding: 14, boxShadow: '0 8px 32px rgba(0,0,0,0.5)',
        }}>
          <div style={{ fontSize: 9, fontWeight: 800, color: 'rgba(148,172,210,0.6)', textTransform: 'uppercase', letterSpacing: '0.12em', marginBottom: 10 }}>
            Platform Status
          </div>
          {DOTS.map((key) => (
            <div key={key} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '5px 0' }}>
              <div style={{ width: 8, height: 8, borderRadius: '50%', background: COLOR[health[key]], flexShrink: 0 }}/>
              <span style={{ flex: 1, fontSize: 12, color: '#e2ecfa' }}>{LABEL[key]}</span>
              <span style={{ fontSize: 10, fontWeight: 700, color: COLOR[health[key]] }}>{health[key]}</span>
            </div>
          ))}
          {lastPoll > 0 && (
            <div style={{ fontSize: 9, color: 'rgba(100,140,180,0.4)', marginTop: 8, textAlign: 'right' }}>
              Updated {new Date(lastPoll).toLocaleTimeString()}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
